import { backendService } from './services/backendService';
import type {
  ApiAdminLoginResponse,
  ApiAdminGuestResponse,
  ApiAdminTenantCreateResponse,
  ApiAdminTenantDetailResponse,
  ApiAdminTenantResponse,
  ApiAdminUserResponse,
  ApiDashboardFullResponse,
  ApiConversationDetailResponse,
  ApiConversationSummaryResponse,
  ApiErrorBody,
  ApiKnowledgeDetailResponse,
  ApiKnowledgeDocumentResponse,
  ApiKnowledgeUpsertRequest,
  ApiPageResponse,
  ApiPersonaResponse,
} from '@/models/model.response';

function extractError(error: unknown): Error {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { data?: ApiErrorBody } }).response?.data?.message === 'string'
  ) {
    const body = (error as { response: { data: ApiErrorBody } }).response.data;
    return new Error(body.message);
  }
  return error instanceof Error ? error : new Error('Request failed');
}

export const apiSource = {
  async adminLogin(username: string, password: string): Promise<ApiAdminLoginResponse> {
    try {
      const response = await backendService.post<ApiAdminLoginResponse>('/admin/auth/login', {
        username,
        password,
      });
      return response.data;
    } catch (error) {
      throw extractError(error);
    }
  },

  async adminLogout(): Promise<void> {
    await backendService.post('/admin/auth/logout');
  },

  async getTenants(includeDisabled = false): Promise<ApiAdminTenantResponse[]> {
    const response = await backendService.get<ApiAdminTenantResponse[]>('/admin/tenants', {
      params: { includeDisabled },
    });
    return response.data;
  },

  async getTenant(id: string): Promise<ApiAdminTenantDetailResponse> {
    const response = await backendService.get<ApiAdminTenantDetailResponse>(`/admin/tenants/${id}`);
    return response.data;
  },

  async createTenant(payload: Record<string, unknown>): Promise<ApiAdminTenantCreateResponse> {
    const response = await backendService.post<ApiAdminTenantCreateResponse>(
      '/admin/tenants',
      payload,
    );
    return response.data;
  },

  async patchTenant(
    id: string,
    payload: Record<string, unknown>,
  ): Promise<ApiAdminTenantDetailResponse> {
    const response = await backendService.patch<ApiAdminTenantDetailResponse>(
      `/admin/tenants/${id}`,
      payload,
    );
    return response.data;
  },

  async getDashboard(): Promise<ApiDashboardFullResponse> {
    const response = await backendService.get<ApiDashboardFullResponse>('/admin/dashboard');
    return response.data;
  },

  async getUsers(
    page: number,
    limit: number,
    searchBy?: 'NAME' | 'EMAIL' | 'STATUS',
    query?: string,
  ): Promise<ApiPageResponse<ApiAdminUserResponse>> {
    const response = await backendService.get<ApiPageResponse<ApiAdminUserResponse>>(
      '/admin/users',
      {
        params: {
          page: page - 1,
          limit,
          ...(query?.trim() ? { searchBy, query: query.trim() } : {}),
        },
      },
    );
    return response.data;
  },

  async patchUser(
    id: string,
    payload: { name?: string; tokenBalance?: number; enabled?: boolean },
  ): Promise<ApiAdminUserResponse> {
    const response = await backendService.patch<ApiAdminUserResponse>(
      `/admin/users/${id}`,
      payload,
    );
    return response.data;
  },

  async deleteUser(id: string): Promise<void> {
    await backendService.delete(`/admin/users/${id}`);
  },

  async getUserConversations(
    userId: string,
    page: number,
    limit: number,
  ): Promise<ApiPageResponse<ApiConversationSummaryResponse>> {
    const response = await backendService.get<ApiPageResponse<ApiConversationSummaryResponse>>(
      `/admin/users/${userId}/conversations`,
      { params: { page: page - 1, limit } },
    );
    return response.data;
  },

  async getConversation(
    userId: string,
    conversationId: string,
    page = 1,
    limit = 50,
  ): Promise<ApiConversationDetailResponse> {
    const response = await backendService.get<ApiConversationDetailResponse>(
      `/admin/users/${userId}/conversations/${conversationId}`,
      { params: { page: page - 1, limit } },
    );
    return response.data;
  },

  async deleteConversation(userId: string, conversationId: string): Promise<void> {
    await backendService.delete(`/admin/users/${userId}/conversations/${conversationId}`);
  },

  async getGuests(page: number, limit: number): Promise<ApiPageResponse<ApiAdminGuestResponse>> {
    const response = await backendService.get<ApiPageResponse<ApiAdminGuestResponse>>(
      '/admin/guests',
      {
        params: { page: page - 1, limit },
      },
    );
    return response.data;
  },

  async deleteGuest(guestId: string): Promise<void> {
    await backendService.delete(`/admin/guests/${guestId}`);
  },

  async patchGuest(
    guestId: string,
    payload: { tokenBalance?: number },
  ): Promise<ApiAdminGuestResponse> {
    const response = await backendService.patch<ApiAdminGuestResponse>(
      `/admin/guests/${guestId}`,
      payload,
    );
    return response.data;
  },

  async getGuestConversations(
    guestId: string,
    page: number,
    limit: number,
  ): Promise<ApiPageResponse<ApiConversationSummaryResponse>> {
    const response = await backendService.get<ApiPageResponse<ApiConversationSummaryResponse>>(
      `/admin/guests/${guestId}/conversations`,
      { params: { page: page - 1, limit } },
    );
    return response.data;
  },

  async getGuestConversation(
    guestId: string,
    conversationId: string,
    page = 1,
    limit = 50,
  ): Promise<ApiConversationDetailResponse> {
    const response = await backendService.get<ApiConversationDetailResponse>(
      `/admin/guests/${guestId}/conversations/${conversationId}`,
      { params: { page: page - 1, limit } },
    );
    return response.data;
  },

  async deleteGuestConversation(guestId: string, conversationId: string): Promise<void> {
    await backendService.delete(`/admin/guests/${guestId}/conversations/${conversationId}`);
  },

  async getKnowledge(
    page: number,
    limit: number,
  ): Promise<ApiPageResponse<ApiKnowledgeDocumentResponse>> {
    const response = await backendService.get<ApiPageResponse<ApiKnowledgeDocumentResponse>>(
      '/admin/knowledge',
      { params: { page: page - 1, limit } },
    );
    return response.data;
  },

  async getKnowledgeDetail(id: string): Promise<ApiKnowledgeDetailResponse> {
    const response = await backendService.get<ApiKnowledgeDetailResponse>(`/admin/knowledge/${id}`);
    return response.data;
  },

  async createKnowledge(payload: ApiKnowledgeUpsertRequest): Promise<ApiKnowledgeDetailResponse> {
    const response = await backendService.post<ApiKnowledgeDetailResponse>(
      '/admin/knowledge',
      payload,
    );
    return response.data;
  },

  async updateKnowledge(
    id: string,
    payload: ApiKnowledgeUpsertRequest,
  ): Promise<ApiKnowledgeDetailResponse> {
    const response = await backendService.put<ApiKnowledgeDetailResponse>(
      `/admin/knowledge/${id}`,
      payload,
    );
    return response.data;
  },

  async deleteKnowledge(id: string): Promise<void> {
    await backendService.delete(`/admin/knowledge/${id}`);
  },

  async getPersona(): Promise<ApiPersonaResponse> {
    const response = await backendService.get<ApiPersonaResponse>('/admin/persona');
    return response.data;
  },

  async updatePersona(payload: {
    name?: string;
    systemPrompt: string;
  }): Promise<ApiPersonaResponse> {
    const response = await backendService.put<ApiPersonaResponse>('/admin/persona', payload);
    return response.data;
  },
};

export function isApiError(error: unknown): error is ApiErrorBody {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as ApiErrorBody).message === 'string'
  );
}
